import React, { memo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./ProductCard.css";
import { FaHeart, FaRegHeart } from "react-icons/fa6";
import { colors } from "../../../../constants/colors";
import {
  getPrice,
  getErrorMessage,
  getOptimizedUrl,
} from "../../../../constants/globalFunction";
import { appApiHandler } from "../../../../api/appApiHandler";
import { setWhishlist } from "../../../../store/slices/whishlistState";
import { showErrorToast } from "../../../../utils/toast";
import { string } from "../../../../constants/string";
import { setWhishlistCount } from "../../../../store/slices/countState";
import { isHoverSupported, RUPEE } from "../../../../constants/globalConstants";

const ProductCard = ({ item }) => {
  const dispatch = useDispatch();
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [isMobileColors, setIsMobileColors] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const colorScrollerRef = useRef(null);
  const colorScrollIndexRef = useRef(0);

  const { userData } = useSelector((state) => state.userState);
  const { whishlist } = useSelector((state) => state.whishlistState);

  const isWishlisted = whishlist?.some(
    (wishItem) => wishItem?.product_id === item?._id
  );

  useEffect(() => {
    if (item?.colors && item?.colors?.length > 0) {
      setSelectedColorId(item?.colors?.[0]?._id);
    } else {
      setSelectedColorId(null);
    }
  }, [item?._id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileColors(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    colorScrollIndexRef.current = 0;
    if (colorScrollerRef.current) {
      colorScrollerRef.current.scrollTo({ left: 0 });
    }
  }, [item?.colors?.length, isMobileColors]);

  const getWishlistProducts = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getWishlist(reqData);
    if (response?.status === 200 && response?.data) {
      dispatch(setWhishlist(response?.data));
      dispatch(setWhishlistCount(response?.data?.length));
    }
  };

  const onPress = () => {
    const productUrl = `/product?product_id=${item?._id}`;
    window.open(productUrl, "_blank");
  };

  const onToggleWishlist = async () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }

    const reqData = {
      user_id: userData?._id,
      product_id: item?._id,
    };

    setWishlistLoading(true);
    const response = isWishlisted
      ? await appApiHandler.removeFromWishlist(reqData)
      : await appApiHandler.addToWishlist(reqData);
    setWishlistLoading(false);

    if (response?.status === 200) {
      getWishlistProducts();
    } else if (response?.message) {
      showErrorToast(getErrorMessage(response?.message));
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const getDisplayImages = () => {
    const hasColors = item?.colors && item?.colors?.length > 0;

    if (!hasColors || !selectedColorId) {
      return item?.image || [];
    }

    const colorImages = (item?.image || []).filter(
      (img) => img?.color?._id === selectedColorId
    );

    if (colorImages.length > 0) {
      return colorImages;
    }

    return item?.image || [];
  };

  const getFirstColorImages = () => {
    const hasColors = item?.colors && item?.colors?.length > 0;

    if (!hasColors) {
      return item?.image || [];
    }

    const firstColorId = item?.colors?.[0]?._id;

    const firstColorImages = (item?.image || []).filter(
      (img) => img?.color?._id === firstColorId
    );

    if (firstColorImages.length > 0) {
      return firstColorImages;
    }

    return item?.image || [];
  };

  const displayImages = getDisplayImages();
  const firstColorImages = getFirstColorImages();

  const handleMouseEnter = () => {
    if (!isHoverSupported) return;
    if (firstColorImages?.length > 1) {
      setIsHovered(true);
    } else if (displayImages?.length > 1) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isHoverSupported) return;
    setIsHovered(false);
  };

  const handleColorClick = (e, colorId) => {
    e.stopPropagation();
    setSelectedColorId(colorId);
    setIsHovered(false);
  };

  const visibleColorCount = isMobileColors ? 2 : 3;
  const swatchStep = 24 + 12;

  useEffect(() => {
    const colorsLength = item?.colors?.length || 0;
    if (!colorScrollerRef.current || colorsLength <= visibleColorCount) {
      return;
    }

    const scroller = colorScrollerRef.current;
    const intervalId = setInterval(() => {
      const maxStartIndex = Math.max(0, colorsLength - visibleColorCount);
      const nextIndex =
        colorScrollIndexRef.current >= maxStartIndex
          ? 0
          : colorScrollIndexRef.current + 1;
      colorScrollIndexRef.current = nextIndex;
      scroller.scrollTo({
        left: nextIndex * swatchStep,
        behavior: "smooth",
      });
    }, 4000);

    return () => clearInterval(intervalId);
  }, [visibleColorCount, item?.colors?.length]);

  useEffect(() => {
    const scroller = colorScrollerRef.current;
    if (!scroller) return;

    const onScroll = () => {
      colorScrollIndexRef.current = Math.round(scroller.scrollLeft / swatchStep);
    };

    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className="productCard"
      onClick={onPress}
      role="button"
      tabIndex={0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className={`productCard__wish ${isWishlisted ? "productCard__wish--wishlisted" : ""
          }`}
        disabled={wishlistLoading}
        aria-pressed={!!isWishlisted}
        onClick={(e) => {
          e.stopPropagation();
          onToggleWishlist();
        }}
      >
        {isWishlisted ? (
          <FaHeart size={15} color={colors.red} />
        ) : (
          <FaRegHeart size={15} color={colors.primary} />
        )}
      </button>
      {displayImages?.length > 0 ? (
        <div className="productCard__image-container">
          {item?.on_sale == 1 && (
            <div className="productCard__sale-badge">Sale</div>
          )}
          <img
            className={`productCard__img ${isHovered &&
              (firstColorImages?.length > 1 || displayImages?.length > 1)
              ? "productCard__img--hidden"
              : ""
              }`}
            src={getOptimizedUrl(displayImages?.[0]?.image)}
            alt={item?.name || "product"}
            loading="lazy"
          />
          {
            isHoverSupported && (
              firstColorImages?.length > 1 || displayImages?.length > 1) && (
              <img
                className={`productCard__img productCard__img--hover ${isHovered ? "productCard__img--visible" : ""
                  }`}
                src={getOptimizedUrl(
                  firstColorImages?.length > 1
                    ? firstColorImages[1]?.image
                    : displayImages[1]?.image
                )}
                alt={item?.name || "product"}
                loading="lazy"
              />
            )
          }
        </div>
      ) : (
        <div className="productCard__img productCard__img--placeholder" />
      )}
      <div className="productCard__content">
        <div className="productCard__name" title={item?.name}>
          {item?.name}
        </div>
        <div className="productCard__price">
          {item?.size?.mrp && parseInt(item?.size?.mrp) > 0 && (
            <span className="productCard__price-mrp">
              {RUPEE}
              {item?.size?.mrp}
            </span>
          )}
          <span className="productCard__price-current">
            {RUPEE}
            {getPrice(item?.size)}
          </span>
        </div>
        {item?.colors && item?.colors?.length > 0 && (
          <div className="productCard__colors-wrapper">
            <div
              className={`productCard__colors ${item?.colors?.length <= visibleColorCount
                ? "productCard__colors--centered"
                : ""
                }`}
              ref={colorScrollerRef}
            >
              {item.colors.map((color) => (
                <div
                  key={color._id}
                  className={"productCard__color-swatch"}
                  style={{ backgroundColor: color.hex_code }}
                  title={color.name}
                  onClick={(e) => handleColorClick(e, color._id)}
                  role="button"
                  tabIndex={0}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ProductCard);
